import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Clock, Shield, Phone, Users, ChevronRight, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { publicAPI } from '../services/api';

// Placeholder component for failed images
const ImagePlaceholder = () => (
  <div className="flex flex-col items-center justify-center h-full text-gray-400">
    <User className="w-16 h-16 mb-2" />
    <span className="text-sm">Photo</span>
  </div>
);

// Consultant card component with safe image error handling
const ConsultantCard = ({ doctor }) => {
  const [imageError, setImageError] = useState(false);
  
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="h-72 overflow-hidden bg-gray-100 flex items-center justify-center">
        {doctor.image && !imageError ? (
          <img
            src={doctor.image}
            alt={doctor.name}
            className={`w-full h-full object-cover ${
              doctor.name?.toLowerCase().includes('awnish') 
                ? 'object-[center_20%]' 
                : 'object-top'
            }`}
            onError={() => setImageError(true)}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className="p-4 text-center">
        <h4 className="font-semibold text-gray-900">{doctor.name}</h4>
        <p className="text-sm text-[#007897]">{doctor.role}</p>
        <p className="text-xs text-gray-500 mt-1">{doctor.qualifications}</p>
      </div>
    </div>
  );
};

const AboutPage = () => {
  const [aboutContent, setAboutContent] = useState({
    hero_image: '',
    title: 'About Nanda Diagnostics',
    intro: '',
    commitment_title: 'Our Commitment to Excellence',
    commitment_text: '',
    technology_title: 'Cutting-Edge Technology Meets Experience',
    technology_text: '',
    legacy_title: 'Our Legacy of Quality Services',
    legacy_text: '',
    closing: ''
  });
  const [directors, setDirectors] = useState([]);
  const [consultants, setConsultants] = useState({ radiology: [], laboratory: [] });
  const [differentiators, setDifferentiators] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch about settings
        const aboutRes = await publicAPI.getAboutSettings();
        if (aboutRes.data) {
          setAboutContent(prev => ({ ...prev, ...aboutRes.data }));
        }

        // Fetch gallery for hero image (use first image)
        const galleryRes = await publicAPI.getGallery();
        if (galleryRes.data && galleryRes.data.length > 0) {
          setAboutContent(prev => ({ 
            ...prev, 
            hero_image: prev.hero_image || galleryRes.data[0].image 
          }));
        }

        // Fetch directors
        const directorsRes = await publicAPI.getDirectors();
        if (directorsRes.data) {
          setDirectors(directorsRes.data);
        }

        // Fetch consultants
        const consultantsRes = await publicAPI.getConsultants();
        if (consultantsRes.data) {
          setConsultants({
            radiology: consultantsRes.data.filter(c => c.department === 'radiology'),
            laboratory: consultantsRes.data.filter(c => c.department === 'laboratory')
          });
        }

        // Fetch differentiators
        const diffRes = await publicAPI.getDifferentiators();
        if (diffRes.data) {
          setDifferentiators(diffRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch about page data:', error);
      }
    };
    fetchData();
  }, []);

  const iconMap = {
    Award: Award,
    Shield: Shield,
    Clock: Clock
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <img
                src={aboutContent.hero_image}
                alt={aboutContent.hero_image_alt || 'About Nanda Diagnostics'}
                className="rounded-2xl shadow-xl w-full h-auto object-cover"
                style={{ maxHeight: '500px' }}
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {aboutContent.title}
              </h1>
              <p className="text-gray-600 leading-relaxed mb-6">
                {aboutContent.intro}
              </p>
              
              {aboutContent.commitment_title && (
                <>
                  <h2 className="text-xl font-semibold text-[#007897] mb-3">
                    {aboutContent.commitment_title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {aboutContent.commitment_text}
                  </p>
                </>
              )}
              
              {aboutContent.technology_title && (
                <>
                  <h2 className="text-xl font-semibold text-[#007897] mb-3">
                    {aboutContent.technology_title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {aboutContent.technology_text}
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Legacy Section */}
          {aboutContent.legacy_title && (
            <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-[#007897] mb-3">
                {aboutContent.legacy_title}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {aboutContent.legacy_text}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {aboutContent.closing}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#007897] py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex items-center justify-center gap-3">
              <Phone className="text-white/80" size={24} />
              <div className="text-left">
                <p className="text-sm text-white/80">Mon to Sat: 8AM to 8PM</p>
                <p className="text-sm text-white/80">Sunday: 8AM to 1PM</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Users className="text-white/80" size={24} />
              <div className="text-left">
                <p className="text-2xl font-bold">100k+</p>
                <p className="text-sm text-white/80">Happy Patients</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Award className="text-white/80" size={24} />
              <div className="text-left">
                <p className="text-2xl font-bold">1000k+</p>
                <p className="text-sm text-white/80">Tests Done</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Phone className="text-white/80" size={24} />
              <div className="text-left">
                <p className="font-semibold">Helpline</p>
                <p className="text-sm text-white/80">011-47500500</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directors Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {directors.map((director, index) => (
            <div 
              key={director.id} 
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 mb-16 last:mb-0`}
            >
              {/* Image */}
              <div className="lg:w-1/3">
                <div className="sticky top-24">
                  {director.image ? (
                    <img
                      src={director.image}
                      alt={director.name}
                      className="w-full max-w-sm mx-auto rounded-2xl shadow-lg object-cover object-top"
                      style={{ height: '450px' }}
                      onError={(e) => { 
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="450" viewBox="0 0 300 450"><rect fill="%23f3f4f6" width="300" height="450" rx="16"/><circle cx="150" cy="140" r="60" fill="%23d1d5db"/><ellipse cx="150" cy="350" rx="100" ry="80" fill="%23d1d5db"/><text fill="%239ca3af" font-family="sans-serif" font-size="14" x="50%25" y="95%25" text-anchor="middle">Photo</text></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full max-w-sm mx-auto rounded-2xl shadow-lg bg-gray-100 flex items-center justify-center" style={{ height: '450px' }}>
                      <div className="text-center text-gray-400">
                        <svg className="w-20 h-20 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                        <span>Photo</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Content */}
              <div className="lg:w-2/3">
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {director.name}
                  </h2>
                  <p className="text-[#007897] font-medium mb-4">{director.role}</p>
                  
                  <div className="space-y-1 mb-6 text-gray-600">
                    <p>{director.qualifications}</p>
                    {director.college && <p className="text-sm">{director.college}</p>}
                  </div>
                  
                  {director.experience && (
                    <p className="text-[#007897] font-medium mb-4">{director.experience}</p>
                  )}
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {director.description}
                  </p>
                  
                  {director.achievements && director.achievements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Experience</h3>
                      <ul className="space-y-2">
                        {director.achievements.map((achievement, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <ChevronRight className="text-[#007897] flex-shrink-0 mt-1" size={16} />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What Makes Us Different
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {differentiators.map((item) => {
              const IconComponent = iconMap[item.icon] || Award;
              return (
                <div key={item.id} className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#007897]/10 rounded-full flex items-center justify-center">
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-12 h-12 object-contain" />
                    ) : (
                      <IconComponent className="text-[#007897]" size={32} />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Know Your Consultant */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Know Your Consultant
          </h2>
          
          {/* Radiology Department */}
          {consultants.radiology.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-[#007897] mb-6 pb-2 border-b-2 border-[#007897]">
                Dept. of Radiology
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {consultants.radiology.map((doctor) => (
                  <ConsultantCard key={doctor.id} doctor={doctor} />
                ))}
              </div>
            </div>
          )}
          
          {/* Laboratory Department */}
          {consultants.laboratory.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-[#007897] mb-6 pb-2 border-b-2 border-[#007897]">
                Dept. of Laboratory Services
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {consultants.laboratory.map((doctor) => (
                  <ConsultantCard key={doctor.id} doctor={doctor} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#007897]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Book Your Test?</h2>
          <p className="text-blue-100 mb-6">
            Experience quality diagnostic services at Nanda Diagnostics
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/packages" 
              className="inline-flex items-center justify-center gap-2 bg-white text-[#007897] px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              View Health Packages
            </Link>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white px-8 py-3 rounded-lg hover:bg-white/10 transition-colors font-medium"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
